import React, { useState, useEffect, useCallback } from 'react';
import type { SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  TextField,
  Pagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ErrorIcon from '@mui/icons-material/Error';
import PersonIcon from '@mui/icons-material/Person';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddItemsModal from './AddItemsModal';
import WantListSelectionModal from './TradeInterestModal';
import WantListEntryListModal from './TradeInterestListModal';
import { getImageSourceLabel, normalizeImageUrls } from '../utils/images';
import { buildEventsApiUrl } from '../config/api';
import { buildAuthHeaders, buildJsonAuthHeaders } from '../utils/authFetch';
import { fetchUserProfileSummary } from '../services/userService';

interface Item {
  id: string;
  name: string;
  category: string;
  description?: string;
  images?: string[];
  tags?: string[];
  addedByUserId?: string;
  addedDate?: string;
  createdByUserId?: string;
  userID?: string;
  isCashItem?: boolean;
  cashValueUSD?: number;
}

interface SavedWantEntry {
  id: string;
  eventID: string;
  targetItemID: string;
  candidateOfferedItemIDs: string[];
  cashOfferUSD?: number;
  status: string;
  requestingUserID?: string;
}

interface EventItemsSectionProps {
  eventId: string;
  userId?: string;
  isCreator: boolean;
  isMember: boolean;
  eventCategory: string;
  eventName: string;
  tradeCutoffPassed: boolean;
  eventItems?: Item[];
  itemsLoading?: boolean;
  itemsError?: string | null;
  onRefreshItems?: () => Promise<void>;
  onItemsUpdate?: () => void | Promise<void>;
}

const EventItemsSection: React.FC<EventItemsSectionProps> = ({
  eventId,
  userId,
  isCreator,
  isMember,
  eventCategory,
  eventName,
  tradeCutoffPassed,
  eventItems,
  itemsLoading,
  itemsError,
  onRefreshItems,
  onItemsUpdate
}) => {
  const navigate = useNavigate();
  const [localItems, setLocalItems] = useState<Item[]>([]);
  const [savedWants, setSavedWants] = useState<SavedWantEntry[]>([]);
  const [incomingWantEntries, setIncomingWantEntries] = useState<SavedWantEntry[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [targetInterestItem, setTargetInterestItem] = useState<Item | null>(null);
  const [viewOffersItem, setViewOffersItem] = useState<Item | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Item | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [removalReason, setRemovalReason] = useState('');
  const [myItemsPage, setMyItemsPage] = useState(1);
  const [otherItemsPage, setOtherItemsPage] = useState(1);
  const [ownerDisplayNames, setOwnerDisplayNames] = useState<Record<string, string>>({});

  const fetchInterests = useCallback(async () => {
    if (!userId) return;
    try {
      const itemsQuery = `?userId=${encodeURIComponent(userId)}`;
      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items${itemsQuery}`), {
        headers: buildAuthHeaders(userId)
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch event items interest summary (${response.status})`);
      }

      const data = await response.json();
      if (data.success) {
        setSavedWants(data.outgoingInterests || []);
        setIncomingWantEntries(data.incomingInterests || []);
      }
    } catch (err) {
      console.warn('⚠️ Failed to load want-list entries', err);
    }
  }, [eventId, userId]);

  const usingExternalItems = Array.isArray(eventItems);
  const items = eventItems ?? localItems;
  const loading = usingExternalItems ? Boolean(itemsLoading) : localLoading;
  const error = usingExternalItems ? (itemsError ?? null) : localError;

  const fetchEventItems = useCallback(async () => {
    if (onRefreshItems) {
      await onRefreshItems();
      return;
    }

    try {
      setLocalLoading(true);
      setLocalError(null);

      const response = await fetch(buildEventsApiUrl(`/api/events/${eventId}/items`), {
        method: 'GET',
        headers: userId ? buildJsonAuthHeaders(userId) : undefined
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch items (${response.status})`);
      }

      const data = await response.json();

      if (data.success && data.items) {
        setLocalItems(data.items);
      } else {
        setLocalError(data.error || 'Failed to load items');
      }
    } catch (err: any) {
      console.error('❌ Error fetching items:', err);
      setLocalError(err.message || 'Failed to load items');
    } finally {
      setLocalLoading(false);
    }
  }, [eventId, onRefreshItems, userId]);

  useEffect(() => {
    if (!usingExternalItems) {
      fetchEventItems();
    }
    fetchInterests();
  }, [fetchEventItems, fetchInterests, usingExternalItems]);

  const getItemOwnerId = (item: Item) => item.addedByUserId || item.createdByUserId || item.userID || '';
  const getOwnerDisplayName = (ownerId: string) => ownerDisplayNames[ownerId] || ownerId;
  const myItems = items.filter((item) => getItemOwnerId(item) === userId);
  const otherItems = items.filter((item) => getItemOwnerId(item) !== userId);

  useEffect(() => {
    const ownerIds = Array.from(new Set(items.map((item) => getItemOwnerId(item)).filter((ownerId) => ownerId && ownerId !== userId)));
    const missingOwnerIds = ownerIds.filter((ownerId) => !ownerDisplayNames[ownerId]);

    if (missingOwnerIds.length === 0) return;

    let cancelled = false;

    Promise.all(
      missingOwnerIds.map(async (ownerId) => {
        try {
          const profile = await fetchUserProfileSummary(ownerId);
          const displayName = profile?.displayName || profile?.userName || ownerId;
          return [ownerId, displayName] as const;
        } catch {
          return [ownerId, ownerId] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      setOwnerDisplayNames((current) => {
        const next = { ...current };
        for (const [ownerId, displayName] of entries) {
          next[ownerId] = displayName;
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [items, ownerDisplayNames, userId]);
  const hasWantForItem = (itemId: string) => savedWants.some(want => want.targetItemID === itemId && want.status === 'active');
  const getExistingWantForItem = (itemId: string) => savedWants.find(want => want.targetItemID === itemId && want.status === 'active');
  const getIncomingWantCount = (itemId: string) => incomingWantEntries.filter(want => want.targetItemID === itemId && want.status === 'active').length;
  const getIncomingWantsForItem = (itemId: string) => incomingWantEntries.filter(want => want.targetItemID === itemId);
  const itemsPerPage = 9;
  const myItemsPageCount = Math.max(1, Math.ceil(myItems.length / itemsPerPage));
  const otherItemsPageCount = Math.max(1, Math.ceil(otherItems.length / itemsPerPage));
  const pagedMyItems = myItems.slice((myItemsPage - 1) * itemsPerPage, myItemsPage * itemsPerPage);
  const pagedOtherItems = otherItems.slice((otherItemsPage - 1) * itemsPerPage, otherItemsPage * itemsPerPage);

  const handleDeleteClick = (item: Item, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(item);
    setRemovalReason('');
    setDeleteDialogOpen(true);
  };

  const handleInterestClick = (item: Item, e: React.MouseEvent) => {
    e.stopPropagation();
    setTargetInterestItem(item);
    setInterestModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    const targetItem = itemToDelete;
    const reason = removalReason.trim();

    try {
      setDeleting(true);
      setLocalError(null);

      const response = await fetch(
        buildEventsApiUrl(`/api/events/${eventId}/items/${targetItem.id}`),
        {
          method: 'DELETE',
          headers: buildJsonAuthHeaders(userId, {
            'Accept': 'application/json'
          }),
          body: JSON.stringify({ userId, removalReason: reason })
        }
      );

      let data: any = null;
      const responseText = await response.text();
      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('❌ Failed to parse remove-item response:', parseError, responseText);
          throw new Error('Server returned an invalid response while removing the item');
        }
      }

      if (!response.ok) {
        throw new Error(data?.error || 'Failed to remove item');
      }

      setDeleteDialogOpen(false);
      setItemToDelete(null);
      setRemovalReason('');
      if (usingExternalItems) {
        await onItemsUpdate?.();
      } else {
        await fetchEventItems();
        await onItemsUpdate?.();
      }
    } catch (err: any) {
      console.error('❌ Error removing item:', err);
      setLocalError(err.message || 'Failed to remove item');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    setMyItemsPage((current) => Math.min(current, myItemsPageCount));
  }, [myItemsPageCount]);

  useEffect(() => {
    setOtherItemsPage((current) => Math.min(current, otherItemsPageCount));
  }, [otherItemsPageCount]);

  const handleAddItemsSuccess = async () => {
    if (usingExternalItems) {
      await onItemsUpdate?.();
      return;
    }

    await fetchEventItems();
    await onItemsUpdate?.();
  };

  const handleInterestSaved = async () => {
    await fetchEventItems();
    await fetchInterests();
  };

  const handleCashItemCreated = async () => {
    await fetchEventItems();
    await fetchInterests();
  };

  const canAddItems = Boolean(userId) && (isCreator || isMember) && !tradeCutoffPassed;
  const canEditWantLists = Boolean(userId) && !tradeCutoffPassed;
  const canRemoveItem = (item: Item) => Boolean(userId) && !tradeCutoffPassed && (isCreator || getItemOwnerId(item) === userId);
  const removingAnotherUsersItem = Boolean(userId && itemToDelete && getItemOwnerId(itemToDelete) && getItemOwnerId(itemToDelete) !== userId);

  const renderItemsGrid = (sectionItems: Item[], emptyText: string, showOwnerLabel: boolean) => {
    if (sectionItems.length === 0) {
      return (
        <Card>
          <CardContent>
            <Typography color="textSecondary">{emptyText}</Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Grid container spacing={2}>
        {sectionItems.map((item) => {
          const itemId = item.id;
          const imageCandidates = [
            (item as any).thumbnailUrl,
            (item as any).thumbnail,
            (item as any).metadata?.thumbnailUrl,
            (item as any).bggThumbnailUrl,
            (item as any).bggThumbnail,
            (item as any).bggMetadata?.thumbnailUrl,
            (item as any).bggMetadata?.imageUrl,
            (item as any).imageUrl,
            (item as any).image,
            (item as any).metadata?.imageUrl,
            (item as any).bggImageUrl,
            (item as any).bggImage,
            ...(Array.isArray(item.images) ? item.images : [])
          ];
          const imageUrls = normalizeImageUrls(imageCandidates);
          const primaryImageUrl = imageUrls[0] || '';
          const primaryImageSource = getImageSourceLabel(
            Array.isArray(item.images) ? item.images[0] : null,
            Boolean((item as any).bggId) && !(Array.isArray(item.images) && (item.images[0] as any)?.source)
          );
          const ownerId = getItemOwnerId(item);
          const wantSaved = hasWantForItem(itemId);
          const incomingCount = getIncomingWantCount(itemId);
          const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
            event.currentTarget.style.display = 'none';
            const fallback = event.currentTarget.parentElement?.querySelector('[data-image-fallback="true"]') as HTMLElement | null;
            if (fallback) fallback.style.display = 'flex';
          };

          return (
            <Grid item xs={12} sm={6} md={4} key={itemId}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                  }
                }}
                onClick={() => navigate(`/events/${eventId}/items/${itemId}`, { state: { item } })}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 200,
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {primaryImageUrl ? (
                    <>
                      <Box
                        component="img"
                        src={primaryImageUrl}
                        alt={item.name || 'Item image'}
                        onError={handleImageError}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      <Box
                        data-image-fallback="true"
                        sx={{
                          display: 'none',
                          width: '100%',
                          height: '100%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          color: '#94a3b8',
                          gap: 1
                        }}
                      >
                        <Inventory2Icon sx={{ fontSize: 36 }} />
                        <Typography variant="caption">Image unavailable</Typography>
                      </Box>
                      <Chip
                        label={primaryImageSource}
                        size="small"
                        sx={{ position: 'absolute', left: 8, bottom: 8, bgcolor: 'background.paper' }}
                      />
                    </>
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        color: '#94a3b8',
                        gap: 1
                      }}
                    >
                      <Inventory2Icon sx={{ fontSize: 36 }} />
                      <Typography variant="caption">No image</Typography>
                    </Box>
                  )}
                </Box>

                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography gutterBottom variant="h6" noWrap>
                      {item.name || 'Unnamed Item'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {showOwnerLabel ? <Chip size="small" label="Other Trader" /> : <Chip size="small" color="primary" label="Mine" />}
                      {item.isCashItem && <Chip size="small" color="warning" label={`Cash $${Number(item.cashValueUSD || 0).toFixed(2)}`} />}
                      {wantSaved && <Chip size="small" color="secondary" label="Want Saved" />}
                      {!showOwnerLabel && incomingCount > 0 && (
                        <Chip
                          size="small"
                          color="success"
                          variant="filled"
                          label={`Wants: ${incomingCount}`}
                        />
                      )}
                    </Box>
                  </Box>

                  {item.addedDate && (
                    <Typography variant="caption" color="textSecondary">
                      Added: {new Date(item.addedDate).toLocaleDateString()}
                    </Typography>
                  )}

                  {ownerId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
                      <PersonIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      <Typography variant="caption" color="textSecondary">
                        {showOwnerLabel ? `Trader: ${getOwnerDisplayName(ownerId)}` : 'Your event item'}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  {showOwnerLabel && (
                    <Button
                      size="small"
                      variant={wantSaved ? 'outlined' : 'contained'}
                      startIcon={<FavoriteBorderIcon />}
                      onClick={(e) => handleInterestClick(item, e)}
                      disabled={!canEditWantLists}
                    >
                      {wantSaved ? 'Edit Want' : 'Add to Want List'}
                    </Button>
                  )}

                  {!showOwnerLabel && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MailOutlineIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewOffersItem(item);
                      }}
                    >
                      View Wants
                    </Button>
                  )}

                  {canRemoveItem(item) && (
                    <Tooltip title="Remove from trade">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(item, e)}
                        disabled={deleting}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Event Trade Pool ({items.length})
        </Typography>
        {canAddItems && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={() => setModalOpen(true)}
          >
            Add Items
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
          {error}
        </Alert>
      )}

      {tradeCutoffPassed && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Trade cutoff has passed. Items and want lists are locked for this event.
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Inventory2Icon color="primary" />
          <Typography variant="h6">My Items in This Event ({myItems.length})</Typography>
        </Box>
        {renderItemsGrid(
          pagedMyItems,
          canAddItems ? 'You have not added any items to this event yet. Use Add Items to bring inventory into this trade.' : 'You have not added any items to this event. If the cutoff has passed, new items can no longer be added.',
          false
        )}
        {myItems.length > itemsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={myItemsPageCount}
              page={myItemsPage}
              onChange={(_, page) => setMyItemsPage(page)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TravelExploreIcon color="primary" />
          <Typography variant="h6">Other People&apos;s Items in This Event ({otherItems.length})</Typography>
        </Box>
        {renderItemsGrid(
          pagedOtherItems,
          'No other attendees have added items to this event yet.',
          true
        )}
        {otherItems.length > itemsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={otherItemsPageCount}
              page={otherItemsPage}
              onChange={(_, page) => setOtherItemsPage(page)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Box>

      <AddItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAddItems={handleAddItemsSuccess}
        eventId={eventId}
        userId={userId || ''}
        eventCategory={eventCategory}
        eventName={eventName}
      />

      <WantListSelectionModal
        open={interestModalOpen}
        onClose={() => setInterestModalOpen(false)}
        onSaved={handleInterestSaved}
        eventId={eventId}
        targetItem={targetInterestItem}
        myItems={myItems}
        userId={userId || ''}
        tradeLocked={tradeCutoffPassed}
        onCashItemCreated={handleCashItemCreated}
        existingInterest={targetInterestItem ? getExistingWantForItem(targetInterestItem.id) || null : null}
      />

      <WantListEntryListModal
        open={Boolean(viewOffersItem)}
        onClose={() => setViewOffersItem(null)}
        itemName={viewOffersItem?.name || 'Item'}
        itemId={viewOffersItem?.id}
        interests={viewOffersItem ? getIncomingWantsForItem(viewOffersItem.id) : []}
      />

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Item?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to remove &quot;{itemToDelete?.name}&quot; from this trade?
          </Typography>
          {removingAnotherUsersItem && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Because this is someone else&apos;s item, you need to leave a note explaining why it&apos;s being removed. They&apos;ll receive it in their mailbox.
              </Alert>
              <TextField
                label="Reason for removal"
                fullWidth
                multiline
                minRows={3}
                value={removalReason}
                onChange={(e) => setRemovalReason(e.target.value)}
                placeholder="Example: This item doesn’t match the event category, violates event rules, or was added by mistake."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting || (removingAnotherUsersItem && !removalReason.trim())}
          >
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventItemsSection;
